"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useShippingOffer = useShippingOffer;
var _shippingOfferDialogResult = require("@ori-events/shipping-offer-dialog-result");
var _checkoutUtils = require("@ori/checkout-utils");
var _logger = require("@ori/logger");
var _react = require("react");
var _useShippingOfferQuery = require("../api/useShippingOfferQuery");
var _logger2 = require("../logger");
var _utils = require("../utils");
var _useNextStepNavigation = require("./useNextStepNavigation");
function useShippingOffer(deliveryChannelId, onClose) {
  const logger = (0, _logger.useLogger)('useShippingOffer');
  const {
    data,
    isLoading
  } = (0, _useShippingOfferQuery.useShippingOfferQuery)(deliveryChannelId);
  const [selection, setSelection] = (0, _react.useState)();
  const [isAddingOffer, setIsAddingOffer] = (0, _react.useState)(false);
  const {
    setWasNavigationToNextStep,
    wasNavigationToNextStep
  } = (0, _useNextStepNavigation.useNextStepNavigation)();
  const isAddButtonEnabled = !isLoading && !isAddingOffer && selection !== undefined;
  const items = (0, _react.useMemo)(() => (isLoading ? [] : data?.items ?? []).map(item => ({
    ...item,
    isDisabled: isAddButtonEnabled ? item.productCode !== selection : false,
    selectedQuantity: isAddButtonEnabled && item.productCode === selection ? 1 : 0
  })), [isAddButtonEnabled, data?.items, isLoading, selection]);
  (0, _logger2.logDebug)('useShippingOffer() hook called.', '\n\tdeliveryChannelId:', deliveryChannelId, '\n\tisLoading', isLoading, '\n\tisAddButtonEnabled', isAddButtonEnabled, '\n\tisAddingOffer', isAddingOffer, '\n\titems', items, '\n\tselection', selection, '\n\twasNavigationToNextStep', wasNavigationToNextStep);

  /**
   * Simple logic ATM for selecting the items:
   * - Max 1 item being selected.
   * - Switching quantity between 0 and 1.
   * - The isDisabled property on particular item doesn't block the toggling now,
   *   just contributes to better highlighting of the selected item.
   */
  const toggleItem = (0, _react.useCallback)(productCode => {
    const toggledItem = items.find(item => item.productCode === productCode);
    if (!toggledItem) {
      return;
    }
    const {
      selectedQuantity
    } = toggledItem;
    const isSelected = selectedQuantity > 0;
    setSelection(isSelected ? undefined : productCode);
  }, [items]);

  /**
   * Adds the selected product code to shopping context, thus applies the free shipping.
   * After the action is done, it optionally displays a temporary success popup notification.
   */
  const handleAddOffer = (0, _react.useCallback)(async () => {
    if (!isAddButtonEnabled) {
      return;
    }
    try {
      setIsAddingOffer(true);
      await (0, _utils.addShippingOffer)(selection);
      (0, _logger2.logDebug)(`Free shipping offer product ${selection} was successfully applied.`);
      // When navigating away, the success notification message seems redundant, so don't show it.
      if (!wasNavigationToNextStep) {
        // TO-DO: success popup & which one?
        (0, _logger2.logDebug)('TO-DO: success notification popup');
      }
      setIsAddingOffer(false);
      if (wasNavigationToNextStep) {
        (0, _utils.dispachShippingOfferDialogResultEvent)({
          result: _shippingOfferDialogResult.Result.Success,
          productCode: selection
        });
        setWasNavigationToNextStep(false);
      }
      onClose({}, 'success');
    } catch (error) {
      (0, _checkoutUtils.showErrorNotification)();
      setIsAddingOffer(false);
      logger.error(error, {
        atcion: `Adding free shipping offer product ${selection}:`
      });
      const message = String(error);
      if (wasNavigationToNextStep) {
        (0, _utils.dispachShippingOfferDialogResultEvent)({
          result: _shippingOfferDialogResult.Result.Error,
          message
        });
        setWasNavigationToNextStep(false);
      }
    }
  }, [isAddButtonEnabled, logger, onClose, selection, setWasNavigationToNextStep, wasNavigationToNextStep]);
  const buttonsProps = {
    isDisabled: !isAddButtonEnabled,
    isLoading: isLoading || isAddingOffer,
    onAddOffer: handleAddOffer
  };
  return {
    buttonsProps,
    handleAddOffer,
    isAddButtonEnabled,
    isAddingOffer,
    isLoading,
    items,
    toggleItem
  };
}